/**
 * "TV'de oynat" — starts this item on a television from the phone in your hand.
 *
 * The most useful thing in the second-screen set, because it removes the interaction it
 * replaces entirely: finding one film with a remote costs dozens of presses through rails,
 * or the on-screen keyboard. This costs a tap on a device that already has the film on
 * screen.
 *
 * ## It appears only when there is a television to send to
 *
 * Presence comes from the realtime channel, so the button is absent when no TV is on
 * rather than present and failing. A control that is visible and does nothing teaches
 * people not to trust the ones next to it.
 *
 * With more than one television it becomes a small picker. With exactly one — the common
 * case — it sends immediately, because asking "which one?" of a household with one TV is a
 * question with one answer.
 */
import { useMemo, useState } from 'react';
import { useSession } from '../store/session';
import { useRealtimeChannel } from '../realtime/RealtimeContext';

export function PlayOnTvButton({ itemId, onCard }: { itemId: string; onCard?: boolean }) {
  const { profile } = useSession();
  const { presence, send } = useRealtimeChannel();
  const [picking, setPicking] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const televisions = useMemo(() => presence.filter((d) => d.deviceType === 'tv'), [presence]);

  if (televisions.length === 0 || !profile) return null;

  const play = (deviceId: string) => {
    const ok = send({
      type: 'playOnTv',
      targetDeviceId: deviceId,
      itemId,
      // The receiving television's own profile is whatever it is signed in as; this names
      // the profile the *request* is made under, which the server verifies belongs to the
      // household before it relays anything.
      profileId: profile.id,
    });
    setPicking(false);
    setSentTo(ok ? deviceId : null);
  };

  /*
   * On a card this sits inside the `<Link>` that opens the item. Without both of these a
   * tap would send the film to the television *and* start it on the phone — the exact
   * double-play the card placement exists to avoid.
   */
  const swallow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (sentTo) {
    return (
      <span className={`playontv playontv--sent${onCard ? ' playontv--card' : ''}`}>
        {onCard ? '✓' : 'Televizyonda açılıyor…'}
      </span>
    );
  }

  if (picking) {
    return (
      <div className="playontv__pick" onClick={swallow}>
        {televisions.map((tv) => (
          <button
            key={tv.deviceId}
            type="button"
            onClick={(e) => {
              swallow(e);
              play(tv.deviceId);
            }}
          >
            {tv.name ?? 'Televizyon'}
          </button>
        ))}
        <button
          type="button"
          className="link"
          onClick={(e) => {
            swallow(e);
            setPicking(false);
          }}
        >
          Vazgeç
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`playontv${onCard ? ' playontv--card' : ''}`}
      title="TV'de oynat"
      aria-label="TV'de oynat"
      onClick={(e) => {
        swallow(e);
        if (televisions.length === 1) play(televisions[0]!.deviceId);
        else setPicking(true);
      }}
    >
      {onCard ? '📺' : "📺 TV'de oynat"}
    </button>
  );
}
